
import { Component, OnInit, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CustomizerComponentHeaderComponent } from '@app/customizer/components';
import { SelectBoxComponent } from '@app/customizer/components/select-box/select-box';
import { ModelGroupModel } from '@models/model-group';
import { ConfigurationService } from '@services/configuration.service';
import { BehaviorSubject } from 'rxjs';
import { ComponentVariantChangeModel } from '@models/component-variant-change';
import { DataApiService } from '@services/data-api.service';
import { EventsService } from '@services/events.service';
import { ImageService, ImageSize } from '@services/image.service';
import { ComponentVariantFromApiModel } from '../../../../_models/api-models/component-variant-fromapi';
import { SorterService } from '../../../../_services/sorter.service';
import { AngleConstants } from '../../../../shared/angle-constants';
import { PreciousFinishService } from '../precious-finish.service';
import { BaseCustomizerComponent } from './../../../../shared/base-customizer-component';

@Component({
	selector: 'nosepad-design-menu',
	template: `
        <customizer-component-header [menu]="menu" (updateCollapsedOnComponent)="updateCollapsed($event)">
          @if (designs.length) {
            <select-box
              [editMode]="state === customizerStateEnum.EDITORDER"
              [imageClass]="'nosepad'"
              [optionsImage]="getImage"
              [options]="designs"
              optionsText="Description"
              optionsId="Id"
              [selected]="getSelectedVariant"
              (selectedChange)="onSelected($event)"
              [optionsDiscontinued]="'Discontinued'"
              (matchColors)="matchColors(null)"
              [colorMatchMode]="true"
              [colorsAvailableForMatching]="allPossibleColorsOnOtherSelectedVariants"
              [currentColor]="getSelectedVariant.VariantColor"
              [variantOrModelIsNew]="modelIsNew"
              [matchColor]="menu.matchColor"
              >
            </select-box>
          }
        </customizer-component-header>
        `,
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CustomizerComponentHeaderComponent, SelectBoxComponent]
})
export class NosepadDesignComponent extends BaseCustomizerComponent implements OnInit {

	public designs: Array<ComponentVariantFromApiModel> = [];

	constructor(
		public override events: EventsService,
		private imageService: ImageService,
		public apiService: DataApiService,
		public sorter: SorterService,
		private preciousFinishService: PreciousFinishService,
		public override configurationService: ConfigurationService
	) {
		super(events, apiService, configurationService);
	}

	async initData() {
		this.events.showLoader(true);

		await this.fetchData();
		this.setImageSliderByName(AngleConstants.Perspective);

		this.events.showLoader(false);
	}

	modelGroupTest: BehaviorSubject<ModelGroupModel | null> = new BehaviorSubject<ModelGroupModel | null>(null);

	onChanges(changes: SimpleChanges): void {
		if (changes['ModelGroup'] && changes['ModelGroup'].currentValue) {
			this.modelGroupTest.next(changes['ModelGroup'].currentValue);
		}
	}

	getImage(variant: ComponentVariantFromApiModel) {
		return this.imageService.GetNosepadImage(variant.ToComponentVariantModel(''), ImageSize.Medium);
	}

	onSelected(selected: ComponentVariantFromApiModel) {
		this.events.showLoader(true);

		const newVariant = selected.ToComponentVariantModel(this.getSelectedVariant.Engraving, this.getSelectedVariant.OrderLineUuid, true);

		// ##### GROSS HACK to glue whatever finish selected in the Finish menu (based on the temple component) to the selected nosepad
		if (selected.Group.Fallback === 'precious metal') {
			let templeComponent = this.variants.find(v => v.Code.includes(';') && v.Component.Code === 'TEMPLE');
			if (templeComponent) {
				let templeFinish = templeComponent?.Code.substring(templeComponent.Code.indexOf(';'));

				if (templeFinish) {
					newVariant.Code = newVariant.Code + templeFinish;
					newVariant.Description2 = templeComponent.Description2;
				}
			}
		}

		const changeModel = new ComponentVariantChangeModel(this.getSelectedVariant, newVariant, selected.VariantSizes)

		this.updateVariantDesign(changeModel);
	}

	async fetchData() {
		this.getSelectedVariant.VariantViewed = true;

		let apidata = await this.configurationService.getVariants(this.configuration?.ModelConfigurationUuid, this.modelGroup, this.getSelectedVariant.Component.Uuid);

		await this.RunDisablingSystem(apidata);

		let newList: Array<ComponentVariantFromApiModel> | undefined = apidata;

		// ##### GROSS HACK to remove model duplicates from the list of available nosepad designs - since it was decided to combine the "design" and "finish" attributes into one property - this however kicks our asses later in the process (see line 86)
		if (apidata?.some(cv => cv.Code.includes(';'))) {
			newList = this.preciousFinishService.removeDuplicates(apidata);

		}
		this.configurationService.currentVariants.set(this.currentVariants);
		this.configurationService.variantsFromApi.set(newList);

		if (this.isLimited) {
			newList = newList?.filter(nosepad => nosepad.Order === 'METAL') // filter silicone if limited
		}
		this.designs = newList?.sort(this.sorter.sortBy(ComponentVariantFromApiModel)) ?? [];
	}

}
